import { reactive, nextTick } from 'vue'
import { hasVisibleEditingForm } from './editingForms'

export const apiConnectionState = reactive({ unavailable: false, pageBlocked: false, preserveForm: false,
  pendingReads: 0, checking: false, reason: 'network', recoveryCount: 0 })

export function isConnectionFailure(error) {
  if (error.__CANCEL__ || error.code === 'ERR_CANCELED') return false
  const status = error.response?.status
  return status === 0 || status >= 500 || (!error.response &&
    ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error.code))
}
const isRead = config => ['get', 'head'].includes(config.method)

export function createApiConnection({ api, router, store, hasForm = hasVisibleEditingForm,
  state = apiConnectionState }) {
  let page = 0, order = 0, disposed = false, retryPromise = null
  const pending = new Set()
  const owner = () => String(store?.state.app.account.profile?.id || '')
  const cancelled = config => Object.assign(new Error('Ожидание загрузки отменено'), {
    code: 'ERR_CANCELED', __CANCEL__: true, config,
  })
  const sync = () => {
    state.pendingReads = pending.size
    state.pageBlocked = pending.size > 0 && !hasForm()
  }
  const reachable = (notify = true) => {
    if (!pending.size) {
      if (notify && state.unavailable) state.recoveryCount = (state.recoveryCount || 0) + 1
      state.unavailable = false; state.pageBlocked = false; state.preserveForm = false
    }
  }
  const cancelPending = () => {
    for (const item of [...pending]) item.cancel()
    sync()
  }
  const requestId = api.interceptors.request.use(config => {
    config._connectionPage ??= page
    config._connectionOwner ??= owner()
    config._connectionOrder ??= ++order
    return config
  })
  const waitForRetry = config => new Promise((resolve, reject) => {
    const cleanup = () => {
      pending.delete(item)
      config.signal?.removeEventListener('abort', item.cancel)
      sync()
    }
    const item = {
      config,
      cancel() { cleanup(); reject(cancelled(config)) },
      async retry() {
        if (config._connectionPage !== page || config._connectionOwner !== owner() || config.signal?.aborted) {
          item.cancel(); return
        }
        try {
          const response = await api({ ...config, _connectionReplay: true,
            _retry: false, _reauthRetry: false, _sessionGeneration: undefined })
          if (!pending.has(item)) return
          // Let the original component update its data before showing the page again.
          resolve(response)
          await nextTick()
          cleanup()
        } catch (error) {
          if (!pending.has(item)) return
          if (!isConnectionFailure(error)) { cleanup(); reject(error) }
          // Keep the original read waiting after another connection failure.
        }
      },
    }
    pending.add(item)
    config.signal?.addEventListener('abort', item.cancel, { once: true })
    sync()
    if (config.signal?.aborted) item.cancel()
  })
  const responseId = api.interceptors.response.use(response => {
    reachable()
    return response
  }, error => {
    // A refresh failure may belong to an original read whose access token was rejected.
    const config = error._connectionOriginalConfig || error.config
    if (!config || disposed || !isConnectionFailure(error)) return Promise.reject(error)
    if (config._connectionPage !== page) return Promise.reject(cancelled(config))
    state.unavailable = true
    state.reason = error.response?.status >= 500 ? 'server' : 'network'
    state.preserveForm = hasForm()
    if (isRead(config) && !config._connectionReplay && !config._connectionProbe && !config._skipSessionRecovery) {
      return waitForRetry(config)
    }
    return Promise.reject(error)
  })
  const removeRoute = router?.afterEach((to, from, failure) => {
    if (!failure && to.fullPath !== from.fullPath) { page++; cancelPending(); state.preserveForm = false }
  })
  const unsubscribe = store?.subscribe?.(mutation => {
    if (mutation.type === 'auth/logout') { page++; cancelPending(); reachable(false) }
  })
  return {
    retry() {
      if (retryPromise || disposed) return retryPromise || Promise.resolve()
      state.checking = true
      retryPromise = (async () => {
        let recovered = false
        if (pending.size) {
          // Dispatch order preserves common sequential filter/search loads after an outage.
          for (const item of [...pending].sort((a, b) => a.config._connectionOrder - b.config._connectionOrder)) {
            if (!pending.has(item)) continue
            await item.retry()
            if (pending.has(item)) break
          }
          recovered = !pending.size
        } else {
          // Probe the real API, not index.html: a static frontend can work while API is down.
          try { await api.get('profile/', { _connectionProbe: true, timeout: 10000 }); recovered = true }
          catch (error) { recovered = error.response?.status > 0 && error.response.status < 500 }
        }
        if (recovered) reachable()
      })().finally(() => { state.checking = false; retryPromise = null })
      return retryPromise
    },
    dispose() {
      disposed = true; page++; cancelPending(); reachable(false)
      removeRoute?.(); unsubscribe?.()
      api.interceptors.request.eject(requestId)
      api.interceptors.response.eject(responseId)
    },
  }
}
